import type { AppEntryLinkOpenMode, NhsAppPageDestination } from '@core/entities/AppEntryContext';
import type { NhsAppRuntimeContext, NhsAppPlatform } from '@core/entities/NhsAppRuntime';
import type { NhsAppGateway } from '@core/ports/NhsAppGateway';
import { getTrustedNavigationTarget, navigateToTrustedTarget } from '@infrastructure/navigation/trustedNavigation';

const nhsAppScriptId = 'inav-nhsapp-js-api';
const nhsAppScriptSource = 'https://www.nhsapp.service.nhs.uk/js/v2/nhsapp.js?v=2026-04-24';

type NhsAppNavigationApi = {
  AppPage: Record<string, string>;
  clearBackAction(): void;
  goToPage(appPage: string): void;
  openBrowserOverlay(uri: string): void;
  openExternalBrowser(uri: string): void;
  setBackAction(backAction: () => void): void;
};

type NhsAppApi = {
  navigation: NhsAppNavigationApi;
  tools: {
    getAppPlatform(): NhsAppPlatform;
    isOpenInNHSApp(): boolean;
  };
};

declare global {
  interface Window {
    nhsapp?: NhsAppApi;
  }
}

export class NhsAppGatewayError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'NhsAppGatewayError';
  }
}

export class InlineNhsAppGateway implements NhsAppGateway {
  private runtimePromise?: Promise<NhsAppRuntimeContext>;

  public async clearBackAction(): Promise<void> {
    const runtime = await this.detectRuntime();
    if (!runtime.isEmbedded || !globalThis.window.nhsapp) {
      return;
    }

    globalThis.window.nhsapp.navigation.clearBackAction();
  }

  public async detectRuntime(): Promise<NhsAppRuntimeContext> {
    this.runtimePromise ??= this.resolveRuntime();
    return this.runtimePromise;
  }

  public async navigateToAppPage(destination: NhsAppPageDestination, browserUri: string): Promise<void> {
    const runtime = await this.detectRuntime();
    if (!runtime.isEmbedded || !globalThis.window.nhsapp) {
      navigateToTrustedTarget(browserUri, '/');
      return;
    }

    const pageMap: Record<NhsAppPageDestination, string> = {
      homePage: globalThis.window.nhsapp.navigation.AppPage.HOME_PAGE,
      messages: globalThis.window.nhsapp.navigation.AppPage.MESSAGES,
      account: globalThis.window.nhsapp.navigation.AppPage.ACCOUNT,
    };

    globalThis.window.nhsapp.navigation.goToPage(pageMap[destination]);
  }

  public async openUri(uri: string, openMode: AppEntryLinkOpenMode): Promise<void> {
    const runtime = await this.detectRuntime();
    const resolvedUri = getTrustedNavigationTarget(uri, '/');

    if (!runtime.isEmbedded || !globalThis.window.nhsapp) {
      navigateToTrustedTarget(resolvedUri, '/');
      return;
    }

    if (openMode === 'externalBrowser') {
      globalThis.window.nhsapp.navigation.openExternalBrowser(this.toAbsoluteUri(resolvedUri));
      return;
    }

    globalThis.window.nhsapp.navigation.openBrowserOverlay(this.toAbsoluteUri(resolvedUri));
  }

  public async setBackAction(backAction: () => void): Promise<void> {
    const runtime = await this.detectRuntime();
    if (!runtime.isEmbedded || !globalThis.window.nhsapp) {
      return;
    }

    globalThis.window.nhsapp.navigation.setBackAction(backAction);
  }

  private async resolveRuntime(): Promise<NhsAppRuntimeContext> {
    try {
      await this.ensureScriptLoaded();
    } catch {
      if (this.isEmbeddedUserAgent()) {
        throw new NhsAppGatewayError('Open this journey from the NHS App again so the supported NHS App controls can be restored.');
      }

      return {
        isEmbedded: false,
        platform: 'none',
        showChrome: true,
        apiReady: false,
      };
    }

    const api = globalThis.window.nhsapp;
    if (!api) {
      if (this.isEmbeddedUserAgent()) {
        throw new NhsAppGatewayError('Open this journey from the NHS App again so the supported NHS App controls can be restored.');
      }

      return {
        isEmbedded: false,
        platform: 'none',
        showChrome: true,
        apiReady: false,
      };
    }

    const isEmbedded = api.tools.isOpenInNHSApp();
    const platform = isEmbedded ? api.tools.getAppPlatform() : 'none';

    return {
      isEmbedded,
      platform,
      showChrome: !isEmbedded,
      apiReady: true,
    };
  }

  private async ensureScriptLoaded(): Promise<void> {
    const existingScript = document.getElementById(nhsAppScriptId) as HTMLScriptElement | null;
    if (existingScript?.dataset.loaded === 'true') {
      return;
    }

    if (existingScript) {
      await new Promise<void>((resolve, reject) => {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load the NHS App JS API.')), { once: true });
      });
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.id = nhsAppScriptId;
      script.async = true;
      script.src = nhsAppScriptSource;
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', () => reject(new Error('Failed to load the NHS App JS API.')), { once: true });
      document.head.appendChild(script);
    });
  }

  private isEmbeddedUserAgent(): boolean {
    const userAgent = globalThis.navigator.userAgent.toLowerCase();
    return userAgent.includes('nhsapp-android/') || userAgent.includes('nhsapp-ios/');
  }

  private toAbsoluteUri(candidate: string): string {
    return candidate.startsWith('/') ? new URL(candidate, globalThis.location.origin).toString() : candidate;
  }
}